import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Avatar,
  Badge,
  CircularProgress,
  Grid,
  Typography,
  Divider,
  Collapse,
  IconButton,
  Stack,
  Checkbox,
  Box,
} from "@mui/material";
import Layout from "../../components/LayoutWithHeader.js";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { useAuth } from "../../context/authContext.js";
import useApi from "../../hooks/useApi.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDateFromTimestamp } from "../../utils/utils.js";
import { toast } from "react-toastify";
import { useDebounce } from "../../hooks/useDebounce.js";
import { useNavigate, useSearchParams } from "react-router-dom";
import { updateProfileInCache } from "../../utils/cacheUtils.js";
import { trackEvent } from "../../utils/analytics.js";
import { resizeToWebp } from "../../utils/imageUtils.js";

export default function SettingsProfile() {
  // Hooks
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const {
    checkUsernameAvailability,
    editProfile,
    deleteUser,
    presignProfilePicture,
    confirmProfilePicture,
    uploadAvatarToCdn,
  } = useApi();
  const queryClient = useQueryClient();

  // New user
  const [searchParams] = useSearchParams();
  const newUser = searchParams.get("newUser") === "true";

  // Fire the Plausible `signup` conversion exactly once when a brand-new account
  // first lands here. The `newUser=true` param persists across the onboarding steps,
  // so a sessionStorage guard stops it re-firing on this page's remounts.
  useEffect(() => {
    if (newUser && !sessionStorage.getItem("signup_tracked")) {
      sessionStorage.setItem("signup_tracked", "true");
      trackEvent("signup");
    }
  }, [newUser]);

  // Username
  const [username, setUsername] = useState(newUser ? "" : user.username);
  const [usernameError, setUsernameError] = useState("");

  const usernameCharacterLimit = 15;
  const USERNAME_REGEX = /^[a-z0-9_]{1,15}$/; // usernames can only be letters, numbers, underscores up to 15 characters

  const debouncedCheckUsernameAvailability = useDebounce(async (username) => {
    const isAvailable = await checkUsernameAvailability(username);
    setUsernameError(isAvailable ? "" : "Username already taken");
  }, 300);

  useEffect(() => {
    return () => {
      debouncedCheckUsernameAvailability.cancel();
    };
  }, [debouncedCheckUsernameAvailability]);

  const handleUsernameChange = async (event) => {
    const newUsername = event.target.value.toLowerCase();
    setUsername(newUsername);

    if (newUsername === user.username) {
      setUsernameError("");
      return;
    }

    if (!USERNAME_REGEX.test(newUsername)) {
      setUsernameError(
        "Username can only include letters, numbers, and underscores",
      );
      return;
    }

    debouncedCheckUsernameAvailability(newUsername);
  };

  // Bio
  const [bio, setBio] = useState(
    newUser
      ? `Curating since ${formatDateFromTimestamp(user.firstJoined, "dropDay")}`
      : user.bio,
  );
  const bioCharacterLimit = 150;

  // Email preference
  const [emailPref, setEmailPref] = useState(user.preferences.email);

  // Delete account
  const [deleteAccountExpanded, setDeleteAccountExpanded] = useState(false);
  const [deleteAccountInput, setDeleteAccountInput] = useState("");
  const isDeleteAccountConfirmed =
    deleteAccountInput.trim().toLowerCase() === "delete";

  const handleDeleteAccountExpanded = () => {
    setDeleteAccountExpanded((prev) => !prev);
    setDeleteAccountInput("");
  };
  const handleDeleteAccountInput = (event) => {
    setDeleteAccountInput(event.target.value);
  };

  // Mutation handling
  const hasUnsavedChanges =
    username !== user.username ||
    bio !== (user.bio || "") ||
    emailPref !== user.preferences.email;

  const editProfileMutation = useMutation({
    mutationFn: editProfile,
    onSuccess: (data) => {
      const usernameChanged = user && user.username !== data.username;
      updateProfileInCache({
        queryClient,
        newUserDetails: data,
        onUserUpdate: updateUser,
        usernameChanged,
      });
      if (newUser) navigate("/settings/preferences?newUser=true");
    },
  });

  // Avatar upload. Separate from editProfileMutation: it fires immediately on file
  // select rather than on Save, and must not be gated by hasUnsavedChanges.
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(null);

  const uploadAvatarMutation = useMutation({
    mutationFn: async ({ blob, contentType }) => {
      const { uploadUrl, filename, cacheControl } =
        await presignProfilePicture(contentType);
      await uploadAvatarToCdn(uploadUrl, blob, { contentType, cacheControl });
      return confirmProfilePicture(filename);
    },
    onSuccess: (data) => {
      updateProfileInCache({
        queryClient,
        newUserDetails: data,
        onUserUpdate: updateUser,
      });
      toast.success("Profile picture updated");
    },
  });

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-selecting the same file next time
    if (!file) return;

    // Resizing happens client-side and isn't an API call, so it doesn't go through
    // the global handleApiError path — give it its own toast rather than letting an
    // unhandled rejection through, or a plain Error being mistaken for "offline".
    let resized;
    try {
      resized = await resizeToWebp(file);
    } catch (error) {
      toast.error(error.message || "Could not process that image");
      return;
    }

    const previewUrl = URL.createObjectURL(resized.blob);
    setAvatarPreviewUrl(previewUrl);

    uploadAvatarMutation.mutate(resized, {
      onSettled: () => {
        URL.revokeObjectURL(previewUrl);
        setAvatarPreviewUrl(null);
      },
    });
  };

  const handleSave = () => {
    if (!username) {
      setUsernameError("Please choose a username");
      return; // Prevent proceeding with save
    }
    editProfileMutation.mutate({
      username,
      bio,
      emailPref,
    });
    setDeleteAccountExpanded(false);
    setDeleteAccountInput("");
    if (!newUser) toast.success("Your profile settings have been saved");
  };

  return (
    <Layout
      title={newUser ? "Let's set up your profile" : "Profile settings"}
      showBackButton={!newUser}
      showMenu={!newUser}
    >
      <Grid container spacing={3}>
        <Grid
          item
          xs={3}
          sx={{ display: "flex", justifyContent: "center", alignItems: "flex-start" }}
        >
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                <IconButton
                  component="label"
                  size="small"
                  aria-label="Change profile picture"
                  disabled={uploadAvatarMutation.isPending}
                  sx={{
                    bgcolor: "background.paper",
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    "&:hover": { bgcolor: "background.paper" },
                  }}
                >
                  <PhotoCameraIcon sx={{ fontSize: 16 }} />
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    hidden
                    onChange={handleAvatarChange}
                  />
                </IconButton>
              }
            >
              <Avatar
                alt={user.givenName}
                src={avatarPreviewUrl || user?.avatar_url}
                imgProps={{ referrerPolicy: "no-referrer" }}
                sx={{
                  width: { xs: 80, md: 120 },
                  height: { xs: 80, md: 120 },
                  bgcolor: "tertiary.main",
                  opacity: uploadAvatarMutation.isPending ? 0.6 : 1,
                }}
              />
            </Badge>
            {uploadAvatarMutation.isPending && (
              <CircularProgress
                size={32}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  mt: "-16px",
                  ml: "-16px",
                }}
              />
            )}
          </Box>
        </Grid>
        <Grid item xs={9}>
          <TextField
            id="username-input"
            margin="dense"
            autoFocus={!!newUser}
            label="Username"
            type="text"
            fullWidth
            variant="standard"
            value={username}
            onChange={handleUsernameChange}
            error={!!usernameError}
            helperText={usernameError || undefined}
            inputProps={{ maxLength: usernameCharacterLimit }}
            FormHelperTextProps={{
              sx: { textAlign: "right" },
            }}
            autoComplete="off"
          />

          <TextField
            id="bio-input"
            margin="dense"
            label="Bio"
            type="text"
            fullWidth
            multiline
            maxRows={3}
            variant="standard"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            helperText={!bio ? null : `${bio.length}/${bioCharacterLimit}`}
            inputProps={{ maxLength: bioCharacterLimit }}
            FormHelperTextProps={{
              sx: { textAlign: "right" },
            }}
          />
        </Grid>

        <Grid item xs={3}>
          <Typography variant="body2" sx={{ textAlign: "right" }}>
            Email <br /> preference
          </Typography>
        </Grid>

        <Grid item xs={9}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
            }}
          >
            <Checkbox
              checked={emailPref}
              onChange={() => setEmailPref(!emailPref)}
              inputProps={{ "aria-label": "controlled" }}
            />
            <Typography variant="body2" sx={{ textWrap: "balance" }}>
              {" "}
              I want to hear about new features (never spam)
            </Typography>
          </Stack>
        </Grid>

        {!newUser && (
          <>
            <Grid item xs={12} sx={{ mb: -1 }}>
              <Divider />
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2" sx={{ textAlign: "right" }}>
                Delete account
              </Typography>
            </Grid>
            <Grid item xs={9} sx={{ mt: { xs: 0, md: -1 } }}>
              <IconButton
                onClick={handleDeleteAccountExpanded}
                size="small"
                aria-expanded={deleteAccountExpanded}
                aria-label="Expand delete account section"
              >
                <ExpandMoreIcon
                  sx={{
                    transform: deleteAccountExpanded
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.3s",
                  }}
                />
              </IconButton>
              <Collapse in={deleteAccountExpanded} sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Your Curate account will be deleted and disconnected from your
                  Google account. This action is irreversible.
                </Typography>
                <Stack
                  direction={{ xs: "row", sm: "row" }}
                  spacing={2}
                  sx={{ mt: 1 }}
                >
                  <TextField
                    id="delete-account-input"
                    fullWidth
                    variant="standard"
                    margin="dense"
                    type="text"
                    placeholder="Type 'delete' to confirm"
                    value={deleteAccountInput}
                    onChange={handleDeleteAccountInput}
                    autoComplete="off"
                  />
                  <Button
                    variant="contained"
                    color="error"
                    disabled={!isDeleteAccountConfirmed}
                    onClick={() => deleteUser()}
                  >
                    Confirm
                  </Button>
                </Stack>
              </Collapse>
            </Grid>
          </>
        )}
      </Grid>
      {!deleteAccountExpanded && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
          }}
        >
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              (!newUser && !hasUnsavedChanges) ||
              editProfileMutation.isPending ||
              !!usernameError ||
              isDeleteAccountConfirmed
            }
          >
            {newUser ? "Continue" : hasUnsavedChanges ? "Save" : "All saved"}
          </Button>
        </Box>
      )}
    </Layout>
  );
}
